import { useEffect, useState, useCallback } from "react";
import {
  Bank, Plus, Trash, Calculator, SealCheck, Buildings, HandCoins, Percent, Scroll, MapPin,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, StatusBadge, StatCard } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { COUNTRIES, countryName } from "@/lib/countries";

const SYMBOLS = { usd: "$", eur: "€", gbp: "£", inr: "₹", kes: "KSh ", cad: "C$", aud: "A$" };
export const money = (n, cur) =>
  `${SYMBOLS[(cur || "usd").toLowerCase()] || `${(cur || "usd").toUpperCase()} `}${Number(n || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })}`;

export const FINANCE_TYPES = [
  ["working_capital", "Working capital"],
  ["term_loan", "Term loan"],
  ["vehicle_fleet", "Vehicle & fleet finance"],
  ["invoice_finance", "Invoice / receivable finance"],
  ["trade_finance", "Trade finance (LC)"],
  ["leasing", "Leasing"],
  ["po_finance", "Purchase-order finance"],
  ["overdraft", "Overdraft / credit line"],
  ["islamic", "Islamic / interest-free finance"],
  ["supplier_credit", "Supplier credit (pay later)"],
  ["p2p", "Peer-to-peer lending"],
  ["equity", "Equity / investor funding"],
  ["grant", "Grant (non-repayable)"],
];
export const ftLabel = (k) => (FINANCE_TYPES.find(([x]) => x === k) || [null, k])[1];
const LENDER_KINDS = [
  ["bank", "Bank"], ["nbfc", "NBFC"], ["microlender", "Microlender"],
  ["development_bank", "Development bank"], ["export_credit", "Export-credit agency"],
  ["broker", "Loan agent / broker"],
];
const kindLabel = (k) => (LENDER_KINDS.find(([x]) => x === k) || [null, k])[1];
const CURRENCY_OPTIONS = ["usd", "eur", "gbp", "inr", "kes", "ngn", "aed", "cad", "aud", "zar"];

const BLANK_SEARCH = { finance_type: "working_capital", amount: 50000, tenor_months: 24, country: "US", annual_turnover: 250000, years_trading: 3 };
const BLANK_LENDER = {
  name: "", kind: "bank", sector: "private", finance_types: ["working_capital"], countries: [],
  city: "", covers_whole_country: true, international: false, currency: "usd", regulator: "",
  licence_no: "", rating: "", contact_person: "", email: "", phone: "", website: "",
  description: "", active: true,
};
const BLANK_APPLY = {
  amount: 0, tenor_months: 24, purpose: "", annual_turnover: 250000, years_trading: 3,
  employees: 5, collateral: "", collateral_value: 0, country: "US", city: "", po_id: "",
};
const BLANK_MATCH = {
  country: "US", annual_turnover: 250000, years_trading: 3, employees: 5,
  women_owned: false, msme: true, finance_type: "",
};

export default function Finance() {
  const { user } = useAuth();
  const [view, setView] = useState("compare");
  const [summary, setSummary] = useState(null);
  const [lenders, setLenders] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loans, setLoans] = useState([]);
  const [schemes, setSchemes] = useState([]);
  const [schemeApps, setSchemeApps] = useState([]);
  const [search, setSearch] = useState({ ...BLANK_SEARCH });
  const [options, setOptions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [dialog, setDialog] = useState(null);
  const [lForm, setLForm] = useState({ ...BLANK_LENDER });
  const [applyTo, setApplyTo] = useState(null);
  const [aForm, setAForm] = useState({ ...BLANK_APPLY });
  const [match, setMatch] = useState({ ...BLANK_MATCH });
  const [matched, setMatched] = useState(null);
  const [schemeApply, setSchemeApply] = useState(null);
  const [sForm, setSForm] = useState({ amount: 0, purpose: "" });
  const [openLoan, setOpenLoan] = useState(null);

  const isAdmin = user?.role === "admin";

  const load = useCallback(async () => {
    const [sm, ln, ap, lo, sc, sa] = await Promise.all([
      api.get("/finance/summary"),
      api.get("/finance/lenders"),
      api.get("/finance/applications"),
      api.get("/finance/loans"),
      api.get("/finance/schemes"),
      api.get("/finance/scheme-applications"),
    ]);
    setSummary(sm.data); setLenders(ln.data); setApplications(ap.data);
    setLoans(lo.data); setSchemes(sc.data); setSchemeApps(sa.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const compare = async () => {
    setSearching(true);
    try {
      const { data } = await api.post("/finance/quotes", {
        finance_type: search.finance_type,
        amount: Number(search.amount),
        tenor_months: Number(search.tenor_months),
        country: search.country || null,
        annual_turnover: Number(search.annual_turnover),
        years_trading: Number(search.years_trading),
      });
      setOptions(data.options);
    } catch (err) {
      setOptions([]);
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSearching(false);
    }
  };

  const saveLender = async (e) => {
    e.preventDefault();
    try {
      await api.post("/finance/lenders", {
        ...lForm,
        rating: lForm.rating === "" ? null : Number(lForm.rating),
      });
      toast.success(isAdmin ? "Lender added to the directory" : "Registered — an admin will verify you");
      setDialog(null);
      setLForm({ ...BLANK_LENDER });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const sendApplication = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/finance/applications", {
        product_id: applyTo.product_id,
        amount: Number(aForm.amount),
        tenor_months: Number(aForm.tenor_months),
        purpose: aForm.purpose,
        annual_turnover: Number(aForm.annual_turnover),
        years_trading: Number(aForm.years_trading),
        employees: Number(aForm.employees),
        collateral: aForm.collateral || null,
        collateral_value: Number(aForm.collateral_value),
        country: aForm.country || null,
        city: aForm.city || null,
        po_id: aForm.po_id || null,
      });
      toast.success(`Application ${data.reference} sent to ${data.lender_name}`);
      setApplyTo(null);
      setView("applications");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const runMatch = async () => {
    try {
      const { data } = await api.post("/finance/schemes/match", {
        ...match,
        annual_turnover: Number(match.annual_turnover),
        years_trading: Number(match.years_trading),
        employees: Number(match.employees),
        finance_type: match.finance_type || null,
      });
      setMatched(data);
      toast.success(`${data.qualifies.length} scheme(s) fit your business`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const sendSchemeApplication = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/finance/scheme-applications", {
        scheme_id: schemeApply.id,
        amount: Number(sForm.amount),
        purpose: sForm.purpose,
        annual_turnover: Number(match.annual_turnover),
        years_trading: Number(match.years_trading),
        employees: Number(match.employees),
        women_owned: match.women_owned,
        msme: match.msme,
      });
      toast.success(`${data.reference} submitted — expected support ${money(data.expected_support, data.currency)}`);
      setSchemeApply(null);
      setSForm({ amount: 0, purpose: "" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const repay = async (loan, row) => {
    try {
      const { data } = await api.post("/payments/finance/checkout", {
        loan_id: loan.id,
        installment_no: row.n,
        origin_url: window.location.origin,
        user_id: user?.id,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const act = async (fn, ok) => {
    try {
      await fn();
      toast.success(ok);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const toggleType = (key) =>
    setLForm((f) => ({
      ...f,
      finance_types: f.finance_types.includes(key)
        ? f.finance_types.filter((t) => t !== key)
        : [...f.finance_types, key],
    }));

  const VIEWS = [
    ["compare", "Compare finance"],
    ["lenders", `Lenders (${lenders.length})`],
    ["applications", `Applications (${applications.length})`],
    ["loans", `My loans (${loans.length})`],
    ["schemes", `Gov schemes (${schemes.length})`],
    ["scheme-apps", `Scheme claims (${schemeApps.length})`],
  ];

  return (
    <div data-testid="finance-page">
      <PageHeader
        overline="Capital"
        title="Finance"
        description="Local banks, NBFCs, microlenders, development banks and export-credit agencies by country — with the government scheme policies that sit behind them. Compare the true cost of credit, apply in a few fields, and run the loan and its repayments here."
        testid="finance-header"
        action={
          user?.role !== "member" && (
            <Button
              data-testid="add-lender-button"
              onClick={() => { setLForm({ ...BLANK_LENDER }); setDialog("lender"); }}
              className="w-full sm:w-auto"
            >
              <Plus size={16} className="mr-1.5" /> {isAdmin ? "Add lender" : "Register as lender"}
            </Button>
          )
        }
      />

      {summary && (
        <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-6">
          <StatCard testid="kpi-lenders" label="Lenders" value={summary.lenders} icon={Bank} />
          <StatCard testid="kpi-products" label="Products" value={summary.products} icon={HandCoins} />
          <StatCard testid="kpi-schemes" label="Gov schemes" value={summary.schemes} icon={Scroll} />
          <StatCard testid="kpi-open-applications" label="Open applications" value={summary.open_applications} tone="primary" />
          <StatCard testid="kpi-borrowed" label="Borrowed" value={money(summary.borrowed)} />
          <StatCard testid="kpi-outstanding" label="Outstanding" value={money(summary.outstanding)} tone={summary.outstanding > 0 ? "danger" : "success"} />
        </div>
      )}

      <Tabs value={view} onValueChange={setView} className="mb-5">
        <TabsList className="w-full justify-start overflow-x-auto" data-testid="finance-views">
          {VIEWS.map(([value, label]) => (
            <TabsTrigger key={value} value={value} data-testid={`fin-view-${value}`} className="whitespace-nowrap">
              {label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {/* ---------------- Compare ---------------- */}
      {view === "compare" && (
        <div className="space-y-5" data-testid="compare-panel">
          <div className="grid gap-3 border border-border bg-white p-4 sm:grid-cols-2 lg:grid-cols-6">
            <div>
              <Label>Finance type</Label>
              <Select value={search.finance_type} onValueChange={(v) => setSearch((s) => ({ ...s, finance_type: v }))}>
                <SelectTrigger data-testid="fin-type-select"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-64">
                  {FINANCE_TYPES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Amount</Label>
              <Input data-testid="fin-amount-input" type="number" min="1" value={search.amount} onChange={(e) => setSearch((s) => ({ ...s, amount: e.target.value }))} />
            </div>
            <div>
              <Label>Tenor (months)</Label>
              <Input data-testid="fin-tenor-input" type="number" min="1" value={search.tenor_months} onChange={(e) => setSearch((s) => ({ ...s, tenor_months: e.target.value }))} />
            </div>
            <div>
              <Label>Country</Label>
              <Select value={search.country} onValueChange={(v) => setSearch((s) => ({ ...s, country: v }))}>
                <SelectTrigger data-testid="fin-country-select"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-64">
                  {COUNTRIES.map(([c, n]) => <SelectItem key={c} value={c}>{n}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Annual turnover</Label>
              <Input data-testid="fin-turnover-input" type="number" min="0" value={search.annual_turnover} onChange={(e) => setSearch((s) => ({ ...s, annual_turnover: e.target.value }))} />
            </div>
            <div className="flex items-end">
              <Button data-testid="compare-finance-button" onClick={compare} disabled={searching} className="w-full">
                <Calculator size={16} className="mr-1.5" /> {searching ? "Pricing…" : "Compare"}
              </Button>
            </div>
          </div>

          {options.length === 0 ? (
            <EmptyState testid="no-finance-options" title="No offers priced yet" description="Set the finance type, amount, tenor and country, then compare. Every option shows the instalment and the real cost of credit." />
          ) : (
            <div className="space-y-3" data-testid="finance-options">
              {options.map((o) => (
                <div key={o.product_id} data-testid={`finance-option-${o.product_id}`} className="flex flex-col gap-3 border border-border bg-white p-4 transition-colors duration-150 hover:border-slate-400 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-semibold text-[#0F172A]">{o.product_name}</span>
                      {o.cheapest && (
                        <span data-testid={`cheapest-${o.product_id}`} className="border border-[#A7E3CE] bg-[#E6F7F1] px-2 py-0.5 text-[0.65rem] font-semibold uppercase tracking-wider text-[#0B7A5B]">
                          Cheapest credit
                        </span>
                      )}
                      {o.lender_sector === "government" && (
                        <span className="border border-[#C7D2FE] bg-[#EEF2FF] px-2 py-0.5 text-[0.65rem] font-semibold uppercase tracking-wider text-[#002FA7]">
                          Government
                        </span>
                      )}
                      {o.verified && (
                        <span className="inline-flex items-center gap-1 text-[0.65rem] font-semibold uppercase tracking-wider text-[#002FA7]">
                          <SealCheck size={13} /> Verified
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500">
                      {o.lender_name} · {kindLabel(o.lender_kind)} · {ftLabel(o.finance_type)} · {o.interest_rate_pct}% p.a. · {o.tenor_months} months
                    </div>
                    <div className="text-xs text-slate-500">
                      Interest {money(o.total_interest, o.currency)} + fee {money(o.processing_fee, o.currency)} =
                      {" "}<span className="font-semibold text-[#0F172A]">cost of credit {money(o.cost_of_credit, o.currency)}</span>
                      {o.collateral_required ? " · collateral required" : " · unsecured"}
                    </div>
                    {o.eligibility_note && <div className="mt-1 text-xs text-slate-400">{o.eligibility_note}</div>}
                  </div>
                  <div className="flex items-center gap-3 sm:shrink-0">
                    <div className="text-right">
                      <div className="font-mono text-base font-bold text-[#002FA7]" data-testid={`emi-${o.product_id}`}>
                        {money(o.emi, o.currency)}
                      </div>
                      <div className="text-[0.65rem] text-slate-400">per month</div>
                    </div>
                    <Button
                      size="sm"
                      data-testid={`apply-${o.product_id}`}
                      onClick={() => {
                        setApplyTo(o);
                        setAForm({
                          ...BLANK_APPLY, amount: o.amount, tenor_months: o.tenor_months,
                          annual_turnover: search.annual_turnover, years_trading: search.years_trading,
                          country: search.country,
                        });
                      }}
                    >
                      Apply
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ---------------- Lenders ---------------- */}
      {view === "lenders" && (
        <div className="space-y-3" data-testid="lenders-panel">
          {lenders.length === 0 ? (
            <EmptyState testid="no-lenders" title="No lenders listed" description="Add the first bank or lender to the directory." />
          ) : lenders.map((l) => (
            <div key={l.id} data-testid={`lender-${l.id}`} className="border border-border bg-white p-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-semibold text-[#0F172A]">{l.name}</span>
                    <span className="text-[0.65rem] font-semibold uppercase tracking-wider text-slate-500">{kindLabel(l.kind)}</span>
                    {l.sector === "government" && (
                      <span className="border border-[#C7D2FE] bg-[#EEF2FF] px-2 py-0.5 text-[0.65rem] font-semibold uppercase tracking-wider text-[#002FA7]">Government</span>
                    )}
                    {l.verified
                      ? <span className="inline-flex items-center gap-1 text-[0.65rem] font-semibold uppercase tracking-wider text-[#002FA7]" data-testid={`lender-verified-${l.id}`}><SealCheck size={13} /> Verified</span>
                      : <StatusBadge value="pending" testid={`lender-unverified-${l.id}`} />}
                  </div>
                  <div className="mt-1 text-xs text-slate-500">{(l.finance_types || []).map(ftLabel).join(" · ")}</div>
                  <div className="text-xs text-slate-500" data-testid={`lender-countries-${l.id}`}>
                    {l.countries?.length ? l.countries.map(countryName).join(", ") : "Every country"}
                    {l.international ? " + international" : ""}
                    {l.regulator ? ` · ${l.regulator}` : ""}
                    {l.rating ? ` · ★ ${l.rating}` : ""}
                  </div>
                  {l.description && <div className="mt-1.5 text-xs text-slate-500">{l.description}</div>}
                  <div className="mt-1.5 text-xs text-slate-600">
                    {[l.contact_person, l.email, l.phone].filter(Boolean).join(" · ") || "No contact on file"}
                  </div>
                  {(l.branches || []).length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2" data-testid={`branches-${l.id}`}>
                      {l.branches.map((b) => (
                        <span key={b.id} className="border border-border bg-slate-50 px-2 py-1 text-[0.7rem] text-slate-600">
                          <MapPin size={11} className="mr-1 inline" />
                          {b.city}, {countryName(b.country)}
                          {b.contact_person ? ` · ${b.contact_person}` : ""}
                          {b.phone ? ` · ${b.phone}` : ""}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex flex-wrap gap-2 sm:shrink-0">
                  {!l.owner_id && user?.role !== "member" && (
                    <Button size="sm" variant="outline" data-testid={`claim-lender-${l.id}`} onClick={() => act(() => api.post(`/finance/lenders/${l.id}/claim`), "Listing claimed — awaiting verification")}>
                      This is us
                    </Button>
                  )}
                  {isAdmin && !l.verified && (
                    <Button size="sm" variant="outline" data-testid={`verify-lender-${l.id}`} onClick={() => act(() => api.post(`/finance/lenders/${l.id}/verify`), "Lender verified")}>
                      Verify
                    </Button>
                  )}
                  {isAdmin && (
                    <Button size="sm" variant="ghost" data-testid={`delete-lender-${l.id}`} onClick={() => act(() => api.delete(`/finance/lenders/${l.id}`), "Lender removed")}>
                      <Trash size={14} />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ---------------- Applications ---------------- */}
      {view === "applications" && (
        <div className="space-y-3" data-testid="applications-panel">
          {applications.length === 0 ? (
            <EmptyState testid="no-applications" title="No applications" description="Compare finance and apply — the lender comes back with terms you can accept." />
          ) : applications.map((a) => (
            <div key={a.id} data-testid={`application-${a.id}`} className="border border-border bg-white p-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-mono text-sm font-bold text-[#002FA7]">{a.reference}</span>
                    <StatusBadge value={a.status} testid={`application-status-${a.id}`} />
                  </div>
                  <div className="mt-1 text-xs text-slate-600">
                    {ftLabel(a.finance_type)} · {a.product_name} · {a.lender_name} · {countryName(a.country)}
                  </div>
                  <div className="text-xs text-slate-500">
                    {money(a.amount, a.currency)} over {a.tenor_months} months · indicative {money(a.indicative_emi, a.currency)}/mo at {a.indicative_rate_pct}%
                  </div>
                  <div className="mt-1 text-xs text-slate-500">{a.purpose}</div>
                  {a.offer && (
                    <div className="mt-2 border border-[#A7E3CE] bg-[#E6F7F1] p-2.5 text-xs text-[#0B7A5B]" data-testid={`offer-${a.id}`}>
                      Offered {money(a.offer.amount, a.currency)} at {a.offer.interest_rate_pct}% over {a.offer.tenor_months} months
                      {" "}— {money(a.offer.emi, a.currency)}/mo, fee {money(a.offer.processing_fee, a.currency)},
                      {" "}total {money(a.offer.total_repayable, a.currency)}
                      {a.offer.conditions ? ` · ${a.offer.conditions}` : ""}
                    </div>
                  )}
                  {a.officer_note && <div className="mt-1 text-xs text-slate-400">Lender: {a.officer_note}</div>}
                </div>
                <div className="flex flex-wrap gap-2 sm:shrink-0">
                  {a.status === "offered" && a.applicant_id === user?.id && (
                    <Button size="sm" data-testid={`accept-terms-${a.id}`} onClick={() => act(() => api.post(`/finance/applications/${a.id}/accept`), "Terms accepted — loan created")}>
                      Accept terms
                    </Button>
                  )}
                  {["submitted", "under_review", "offered"].includes(a.status) && a.applicant_id === user?.id && (
                    <Button size="sm" variant="ghost" data-testid={`withdraw-${a.id}`} onClick={() => act(() => api.post(`/finance/applications/${a.id}/withdraw`), "Application withdrawn")}>
                      Withdraw
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ---------------- Loans ---------------- */}
      {view === "loans" && (
        <div className="space-y-3" data-testid="loans-panel">
          {loans.length === 0 ? (
            <EmptyState testid="no-loans" title="No loans yet" description="Accepted terms become a live loan with a repayment schedule you can pay online." />
          ) : loans.map((l) => (
            <div key={l.id} data-testid={`loan-${l.id}`} className="border border-border bg-white p-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-mono text-sm font-bold text-[#002FA7]">{l.loan_number}</span>
                    <StatusBadge value={l.status === "awaiting_drawdown" ? "pending" : l.status} testid={`loan-status-${l.id}`} />
                  </div>
                  <div className="mt-1 text-xs text-slate-600">
                    {ftLabel(l.finance_type)} · {l.product_name} · {l.lender_name}
                  </div>
                  <div className="text-xs text-slate-500">
                    {money(l.principal, l.currency)} at {l.interest_rate_pct}% over {l.tenor_months} months ·
                    {" "}{money(l.emi, l.currency)}/mo · repayable {money(l.total_repayable, l.currency)} ·
                    {" "}outstanding <span className="font-semibold text-[#0F172A]">{money(l.outstanding, l.currency)}</span>
                  </div>
                </div>
                <Button size="sm" variant="outline" data-testid={`open-schedule-${l.id}`} onClick={() => setOpenLoan(openLoan === l.id ? null : l.id)}>
                  {openLoan === l.id ? "Hide schedule" : "Schedule"}
                </Button>
              </div>
              {openLoan === l.id && (
                <div className="mt-3 space-y-1.5 border-t border-border pt-3" data-testid={`schedule-${l.id}`}>
                  {(l.schedule || []).map((row) => (
                    <div key={row.n} data-testid={`instalment-${l.id}-${row.n}`} className="flex flex-col gap-2 bg-slate-50 p-2.5 text-xs sm:flex-row sm:items-center sm:justify-between">
                      <span className="text-slate-600">
                        #{row.n} · due {row.due_date} · {money(row.instalment, l.currency)}
                        {" "}(principal {money(row.principal, l.currency)} + interest {money(row.interest, l.currency)}) · balance {money(row.balance, l.currency)}
                      </span>
                      {row.paid ? (
                        <span className="text-[0.65rem] font-semibold uppercase tracking-wider text-[#0B7A5B]" data-testid={`paid-${l.id}-${row.n}`}>Paid</span>
                      ) : l.status === "active" && l.borrower_id === user?.id ? (
                        <Button size="sm" data-testid={`repay-${l.id}-${row.n}`} onClick={() => repay(l, row)}>Pay</Button>
                      ) : null}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ---------------- Schemes ---------------- */}
      {view === "schemes" && (
        <div className="space-y-5" data-testid="schemes-panel">
          <div className="grid gap-3 border border-border bg-white p-4 sm:grid-cols-2 lg:grid-cols-5">
            <div>
              <Label>Country</Label>
              <Select value={match.country} onValueChange={(v) => setMatch((m) => ({ ...m, country: v }))}>
                <SelectTrigger data-testid="match-country-select"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-64">
                  {COUNTRIES.map(([c, n]) => <SelectItem key={c} value={c}>{n}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Annual turnover</Label>
              <Input data-testid="match-turnover-input" type="number" min="0" value={match.annual_turnover} onChange={(e) => setMatch({ ...match, annual_turnover: e.target.value })} />
            </div>
            <div>
              <Label>Years trading</Label>
              <Input data-testid="match-years-input" type="number" min="0" value={match.years_trading} onChange={(e) => setMatch({ ...match, years_trading: e.target.value })} />
            </div>
            <div className="flex flex-col justify-end gap-1.5">
              <label className="flex cursor-pointer items-center gap-2 text-xs text-slate-600">
                <input type="checkbox" data-testid="match-women-checkbox" checked={match.women_owned} onChange={(e) => setMatch({ ...match, women_owned: e.target.checked })} className="h-4 w-4 accent-[#002FA7]" />
                Women-owned
              </label>
              <label className="flex cursor-pointer items-center gap-2 text-xs text-slate-600">
                <input type="checkbox" data-testid="match-msme-checkbox" checked={match.msme} onChange={(e) => setMatch({ ...match, msme: e.target.checked })} className="h-4 w-4 accent-[#002FA7]" />
                MSME / small business
              </label>
            </div>
            <div className="flex items-end">
              <Button data-testid="match-schemes-button" onClick={runMatch} className="w-full">
                <Percent size={16} className="mr-1.5" /> What do I qualify for?
              </Button>
            </div>
          </div>

          {matched && (
            <div className="border border-[#C7D2FE] bg-[#EEF2FF] p-3 text-xs text-[#002FA7]" data-testid="match-result">
              {matched.qualifies.length} of {matched.checked} schemes fit your business
              {matched.nearly.length > 0 && ` · ${matched.nearly.length} nearly fit`}
            </div>
          )}

          {(matched ? [...matched.qualifies, ...matched.nearly] : schemes).map((s) => (
            <div key={s.id} data-testid={`scheme-${s.id}`} className="flex flex-col gap-3 border border-border bg-white p-4 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-semibold text-[#0F172A]">{s.name}</span>
                  <span className="text-[0.65rem] font-semibold uppercase tracking-wider text-slate-500">{countryName(s.country)}</span>
                  {s.why_not
                    ? <StatusBadge value="pending" testid={`scheme-nearly-${s.id}`} />
                    : matched && <span className="border border-[#A7E3CE] bg-[#E6F7F1] px-2 py-0.5 text-[0.65rem] font-semibold uppercase tracking-wider text-[#0B7A5B]" data-testid={`scheme-qualifies-${s.id}`}>You qualify</span>}
                </div>
                <div className="mt-1 text-xs text-slate-600">
                  <Buildings size={12} className="mr-1 inline" />{s.authority} · {(s.finance_types || []).map(ftLabel).join(" · ")}
                </div>
                <div className="text-xs text-slate-500">
                  {s.subsidy_pct}% support · up to {money(s.ceiling_amount, s.currency)}
                  {s.min_turnover ? ` · turnover from ${money(s.min_turnover, s.currency)}` : ""}
                  {s.max_turnover ? ` to ${money(s.max_turnover, s.currency)}` : ""}
                  {s.women_owned_only ? " · women-owned only" : ""}
                  {s.msme_only ? " · MSME only" : ""}
                </div>
                {s.description && <div className="mt-1 text-xs text-slate-500">{s.description}</div>}
                {s.why_not && <div className="mt-1 text-xs text-[#B3241B]">Not yet: {s.why_not.join("; ")}</div>}
                {s.link && (
                  <a href={s.link} target="_blank" rel="noreferrer" data-testid={`scheme-link-${s.id}`} className="mt-1 inline-block text-xs font-semibold text-[#002FA7] underline">
                    Official scheme page
                  </a>
                )}
              </div>
              {!s.why_not && (
                <Button
                  size="sm"
                  data-testid={`apply-scheme-${s.id}`}
                  onClick={() => { setSchemeApply(s); setSForm({ amount: s.ceiling_amount || 0, purpose: "" }); }}
                >
                  Apply
                </Button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ---------------- Scheme applications ---------------- */}
      {view === "scheme-apps" && (
        <div className="space-y-3" data-testid="scheme-apps-panel">
          {schemeApps.length === 0 ? (
            <EmptyState testid="no-scheme-apps" title="No scheme claims" description="Apply to a scheme you qualify for and track the authority's decision here." />
          ) : schemeApps.map((s) => (
            <div key={s.id} data-testid={`scheme-app-${s.id}`} className="border border-border bg-white p-4">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-sm font-bold text-[#002FA7]">{s.reference}</span>
                <StatusBadge value={s.status} testid={`scheme-app-status-${s.id}`} />
              </div>
              <div className="mt-1 text-xs text-slate-600">
                {s.scheme_name} · {s.authority} · {countryName(s.country)}
              </div>
              <div className="text-xs text-slate-500">
                Asked {money(s.amount, s.currency)} · {s.subsidy_pct}% support · expected {money(s.expected_support, s.currency)}
                {s.approved_amount ? ` · approved ${money(s.approved_amount, s.currency)}` : ""}
              </div>
              <div className="mt-1 text-xs text-slate-500">{s.purpose}</div>
              {s.officer_note && <div className="mt-1 text-xs text-slate-400">Officer: {s.officer_note}</div>}
            </div>
          ))}
        </div>
      )}

      {/* ---------------- Lender dialog ---------------- */}
      <Dialog open={dialog === "lender"} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="lender-dialog">
          <DialogHeader>
            <DialogTitle>{isAdmin ? "Add a lender" : "Register as a lender"}</DialogTitle>
            <DialogDescription>Private or government, which countries you lend in and what you fund.</DialogDescription>
          </DialogHeader>
          <form onSubmit={saveLender} className="space-y-3">
            <div>
              <Label>Institution name</Label>
              <Input data-testid="lender-name-input" required value={lForm.name} onChange={(e) => setLForm({ ...lForm, name: e.target.value })} />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label>Kind</Label>
                <Select value={lForm.kind} onValueChange={(v) => setLForm({ ...lForm, kind: v })}>
                  <SelectTrigger data-testid="lender-kind-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {LENDER_KINDS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Sector</Label>
                <Select value={lForm.sector} onValueChange={(v) => setLForm({ ...lForm, sector: v })}>
                  <SelectTrigger data-testid="lender-sector-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="private">Private</SelectItem>
                    <SelectItem value="government">Government</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Country</Label>
                <Select value={lForm.countries[0] || "ALL"} onValueChange={(v) => setLForm({ ...lForm, countries: v === "ALL" ? [] : [v] })}>
                  <SelectTrigger data-testid="lender-country-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-64">
                    <SelectItem value="ALL">Every country</SelectItem>
                    {COUNTRIES.map(([c, n]) => <SelectItem key={c} value={c}>{n}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>City / branch seat</Label>
                <Input data-testid="lender-city-input" value={lForm.city} onChange={(e) => setLForm({ ...lForm, city: e.target.value })} />
              </div>
              <div>
                <Label>Currency</Label>
                <Select value={lForm.currency} onValueChange={(v) => setLForm({ ...lForm, currency: v })}>
                  <SelectTrigger data-testid="lender-currency-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CURRENCY_OPTIONS.map((c) => <SelectItem key={c} value={c}>{c.toUpperCase()}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Regulator</Label>
                <Input data-testid="lender-regulator-input" value={lForm.regulator} onChange={(e) => setLForm({ ...lForm, regulator: e.target.value })} />
              </div>
              <div>
                <Label>Contact person</Label>
                <Input data-testid="lender-contact-input" value={lForm.contact_person} onChange={(e) => setLForm({ ...lForm, contact_person: e.target.value })} />
              </div>
              <div>
                <Label>Phone</Label>
                <Input data-testid="lender-phone-input" value={lForm.phone} onChange={(e) => setLForm({ ...lForm, phone: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>What you fund</Label>
              <div className="mt-1 flex flex-wrap gap-2" data-testid="lender-finance-types">
                {FINANCE_TYPES.map(([k, l]) => (
                  <button
                    type="button"
                    key={k}
                    data-testid={`lender-ft-${k}`}
                    onClick={() => toggleType(k)}
                    className={`border px-2.5 py-1.5 text-xs font-semibold transition-colors duration-150 ${
                      lForm.finance_types.includes(k)
                        ? "border-[#002FA7] bg-[#002FA7] text-white"
                        : "border-border bg-white text-slate-600 hover:border-slate-400"
                    }`}
                  >
                    {l}
                  </button>
                ))}
              </div>
            </div>
            <label className="flex cursor-pointer items-center gap-2 text-xs text-slate-600">
              <input type="checkbox" data-testid="lender-international-checkbox" checked={lForm.international} onChange={(e) => setLForm({ ...lForm, international: e.target.checked })} className="h-4 w-4 accent-[#002FA7]" />
              Lends cross-border / internationally
            </label>
            <div>
              <Label>About</Label>
              <Textarea data-testid="lender-description-input" rows={2} value={lForm.description} onChange={(e) => setLForm({ ...lForm, description: e.target.value })} />
            </div>
            <DialogFooter>
              <Button type="submit" data-testid="submit-lender-button" className="w-full sm:w-auto">Save lender</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ---------------- Apply dialog ---------------- */}
      <Dialog open={!!applyTo} onOpenChange={(v) => !v && setApplyTo(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="apply-dialog">
          <DialogHeader>
            <DialogTitle>Apply for finance</DialogTitle>
            <DialogDescription>
              {applyTo ? `${applyTo.product_name} · ${applyTo.lender_name} · ${applyTo.interest_rate_pct}% p.a.` : ""}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={sendApplication} className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label>Amount</Label>
                <Input data-testid="apply-amount-input" type="number" min="1" required value={aForm.amount} onChange={(e) => setAForm({ ...aForm, amount: e.target.value })} />
              </div>
              <div>
                <Label>Tenor (months)</Label>
                <Input data-testid="apply-tenor-input" type="number" min="1" required value={aForm.tenor_months} onChange={(e) => setAForm({ ...aForm, tenor_months: e.target.value })} />
              </div>
              <div>
                <Label>Annual turnover</Label>
                <Input data-testid="apply-turnover-input" type="number" min="0" value={aForm.annual_turnover} onChange={(e) => setAForm({ ...aForm, annual_turnover: e.target.value })} />
              </div>
              <div>
                <Label>Years trading</Label>
                <Input data-testid="apply-years-input" type="number" min="0" value={aForm.years_trading} onChange={(e) => setAForm({ ...aForm, years_trading: e.target.value })} />
              </div>
              <div>
                <Label>Employees</Label>
                <Input data-testid="apply-employees-input" type="number" min="0" value={aForm.employees} onChange={(e) => setAForm({ ...aForm, employees: e.target.value })} />
              </div>
              <div>
                <Label>City</Label>
                <Input data-testid="apply-city-input" value={aForm.city} onChange={(e) => setAForm({ ...aForm, city: e.target.value })} />
              </div>
              <div>
                <Label>Collateral offered</Label>
                <Input data-testid="apply-collateral-input" value={aForm.collateral} onChange={(e) => setAForm({ ...aForm, collateral: e.target.value })} />
              </div>
              <div>
                <Label>Collateral value</Label>
                <Input data-testid="apply-collateral-value-input" type="number" min="0" value={aForm.collateral_value} onChange={(e) => setAForm({ ...aForm, collateral_value: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>What the money is for</Label>
              <Textarea data-testid="apply-purpose-input" rows={3} required value={aForm.purpose} onChange={(e) => setAForm({ ...aForm, purpose: e.target.value })} />
            </div>
            {applyTo?.collateral_required && (
              <div className="border border-[#F5C9C5] bg-[#FDECEA] p-2.5 text-xs text-[#B3241B]" data-testid="collateral-warning">
                This product is secured — collateral details are required. {applyTo.collateral_note}
              </div>
            )}
            <DialogFooter>
              <Button type="submit" data-testid="submit-application-button" className="w-full sm:w-auto">Send application</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ---------------- Scheme apply dialog ---------------- */}
      <Dialog open={!!schemeApply} onOpenChange={(v) => !v && setSchemeApply(null)}>
        <DialogContent className="sm:max-w-md" data-testid="scheme-apply-dialog">
          <DialogHeader>
            <DialogTitle>Apply to {schemeApply?.name}</DialogTitle>
            <DialogDescription>
              {schemeApply ? `${schemeApply.authority} · ${schemeApply.subsidy_pct}% support up to ${money(schemeApply.ceiling_amount, schemeApply.currency)}` : ""}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={sendSchemeApplication} className="space-y-3">
            <div>
              <Label>Amount you are asking for</Label>
              <Input data-testid="scheme-amount-input" type="number" min="1" required value={sForm.amount} onChange={(e) => setSForm({ ...sForm, amount: e.target.value })} />
            </div>
            <div>
              <Label>What it will be used for</Label>
              <Textarea data-testid="scheme-purpose-input" rows={3} required value={sForm.purpose} onChange={(e) => setSForm({ ...sForm, purpose: e.target.value })} />
            </div>
            <DialogFooter>
              <Button type="submit" data-testid="submit-scheme-application-button" className="w-full sm:w-auto">Submit claim</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
